import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    IconButton,
    TextField,
    MenuItem,
} from '@mui/material';
import { Close as CloseIcon } from '@material-ui/icons';
import useStyles from '../../../../assets/styles/ManageClassesStyles';
import {
    MAX_SECTION_NAME_LEN,
    MAX_SECTION_LEVEL_LEN,
    MAX_PERSON_NAME_LEN,
    MAX_EMAIL_LEN,
} from '../../../../utils/classCreateValidation';

const GRADE_LEVEL_OPTIONS = ['Kindergarten', ...Array.from({ length: 12 }, (_, i) => `Grade ${i + 1}`)];

// Same rolling window as Add a Class (AddClassStudentsDialog).
const CURRENT_YEAR = new Date().getFullYear();
const ACADEMIC_YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => {
    const start = CURRENT_YEAR - 5 + i;
    return `${start}-${start + 1}`;
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NAME_RE = /^[\p{L}][\p{L}\s'’.-]*$/u;

const nameError = (value) => {
    const v = String(value || '').trim();
    if (!v) return '';
    if (v.length < 2) return 'Too short';
    if (!NAME_RE.test(v)) return 'Letters only';
    return '';
};

const emailError = (value) => {
    const v = String(value || '').trim();
    if (!v) return '';
    return EMAIL_RE.test(v) ? '' : 'Invalid email';
};

/**
 * Standalone "Edit class" dialog — class details only. Adding/managing the
 * class's own students is a separate flow (onManageStudents opens the
 * existing Add/Existing-Students dialog) — this dialog never touches the
 * roster.
 *
 * LearnCreator teachers are no longer edited here: they are added, assigned to
 * classes and published from Manage LearnCreator Teachers (My Classes deck,
 * 30 Sep 2026, slides 25-28 "Remove").
 */
const EditClassDialog = ({
    open,
    onClose,
    classRow,
    onSave,
    onManageStudents,
    saveDisabled,
}) => {
    const classes = useStyles();
    const [className, setClassName] = useState('');
    const [gradeLevel, setGradeLevel] = useState('');
    const [academicYear, setAcademicYear] = useState('');
    const [teacherFirstName, setTeacherFirstName] = useState('');
    const [teacherLastName, setTeacherLastName] = useState('');
    const [teacherEmail, setTeacherEmail] = useState('');

    useEffect(() => {
        if (!open) return;
        setClassName(String(classRow?.name || ''));
        setGradeLevel(String(classRow?.grade || '').trim() === '—' ? '' : String(classRow?.grade || ''));
        setAcademicYear(String(classRow?.academicYear || ''));
        setTeacherFirstName(String(classRow?.teacherFirstName || ''));
        setTeacherLastName(String(classRow?.teacherLastName || ''));
        setTeacherEmail(String(classRow?.teacherEmail || ''));
    }, [open, classRow]);

    const fieldErrors = {
        className: !className.trim() ? 'Required' : '',
        gradeLevel: !gradeLevel.trim() ? 'Required' : '',
        teacherFirstName: !teacherFirstName.trim() ? 'Required' : nameError(teacherFirstName),
        teacherLastName: !teacherLastName.trim() ? 'Required' : nameError(teacherLastName),
        teacherEmail: !teacherEmail.trim() ? 'Required' : emailError(teacherEmail),
    };
    const hasBlockingError = Object.values(fieldErrors).some(Boolean);

    const handleSave = () => {
        if (hasBlockingError || saveDisabled) return;
        onSave({
            className: className.trim(),
            gradeLevel: gradeLevel.trim(),
            academicYear: academicYear.trim(),
            teacherFirstName: teacherFirstName.trim(),
            teacherLastName: teacherLastName.trim(),
            teacherEmail: teacherEmail.trim(),
        });
    };

    if (!classRow) return null;

    // A year saved outside the rolling window still has to show, or opening and
    // saving the class would silently drop it.
    const yearOptions = academicYear && !ACADEMIC_YEAR_OPTIONS.includes(academicYear)
        ? [academicYear, ...ACADEMIC_YEAR_OPTIONS]
        : ACADEMIC_YEAR_OPTIONS;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            classes={{ paper: classes.editClassDialogPaper }}
            maxWidth="sm"
            fullWidth
            BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.4)' } }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '20px', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography className={classes.dialogTitle} style={{ marginBottom: 0, textAlign: 'left', whiteSpace: 'nowrap' }}>
                        Edit Class Information
                    </Typography>
                    <Typography style={{ color: '#aaa', fontSize: '13px', margin: 0 }}>
                        {classRow?.name || ''}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} style={{ marginLeft: 12, flexShrink: 0 }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box className={classes.studentsDialogScroll} sx={{ pt: '16px' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: '12px', mb: '12px' }}>
                    <TextField
                        label="Class Name *"
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={className}
                        onChange={(e) => setClassName(e.target.value)}
                        placeholder="Class name"
                        inputProps={{ maxLength: MAX_SECTION_NAME_LEN }}
                    />
                    <TextField
                        label="Grade Level *"
                        select
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={gradeLevel}
                        onChange={(e) => setGradeLevel(e.target.value)}
                        inputProps={{ maxLength: MAX_SECTION_LEVEL_LEN }}
                    >
                        {GRADE_LEVEL_OPTIONS.map((g) => (
                            <MenuItem key={g} value={g}>{g}</MenuItem>
                        ))}
                    </TextField>
                    {/* Optional, as on Add a Class (LA-SW-07-Oct-01). */}
                    <TextField
                        label="Academic Year"
                        select
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={academicYear}
                        onChange={(e) => setAcademicYear(e.target.value)}
                    >
                        {yearOptions.map((y) => (
                            <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: '12px', mb: '12px' }}>
                    <TextField
                        label="Teacher First Name *"
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={teacherFirstName}
                        onChange={(e) => setTeacherFirstName(e.target.value)}
                        placeholder="First name"
                        inputProps={{ maxLength: MAX_PERSON_NAME_LEN }}
                        error={!!nameError(teacherFirstName)}
                        helperText={nameError(teacherFirstName)}
                    />
                    <TextField
                        label="Teacher Last Name *"
                        variant="outlined"
                        size="small"
                        className={classes.classInfoField}
                        value={teacherLastName}
                        onChange={(e) => setTeacherLastName(e.target.value)}
                        placeholder="Last name"
                        inputProps={{ maxLength: MAX_PERSON_NAME_LEN }}
                        error={!!nameError(teacherLastName)}
                        helperText={nameError(teacherLastName)}
                    />
                </Box>
                <Box sx={{ mb: '20px' }}>
                    <TextField
                        label="Teacher Email *"
                        variant="outlined"
                        size="small"
                        fullWidth
                        className={classes.classInfoField}
                        value={teacherEmail}
                        onChange={(e) => setTeacherEmail(e.target.value)}
                        placeholder="Email address"
                        inputProps={{ maxLength: MAX_EMAIL_LEN }}
                        error={!!emailError(teacherEmail)}
                        helperText={emailError(teacherEmail)}
                    />
                </Box>
            </Box>

            <Box className={classes.studentsDialogFooter}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        onClick={onManageStudents}
                        sx={{
                            textTransform: 'none', fontWeight: 600, fontSize: '14.5px', borderRadius: '50px',
                            color: 'var(--brand-text, #374151)', borderColor: 'var(--brand-border, #D1D5DB)',
                            padding: '10px 24px',
                        }}
                    >
                        Manage Students
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{
                                textTransform: 'none', fontWeight: 600, fontSize: '14.5px', borderRadius: '50px',
                                color: 'var(--brand-text, #374151)', borderColor: 'var(--brand-border, #D1D5DB)',
                                padding: '10px 24px',
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            className={classes.saveAllBtn}
                            onClick={handleSave}
                            disabled={!!saveDisabled || hasBlockingError}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default EditClassDialog;
